import React from 'react';
import { Lightbulb, Edit, Zap, Target, TrendingUp, MessageSquare } from 'lucide-react';

interface ChatQuickActionsProps {
  onSendMessage: (message: string) => void;
  format?: string;
  disabled?: boolean;
  className?: string;
}

export const ChatQuickActions: React.FC<ChatQuickActionsProps> = ({
  onSendMessage,
  format = '',
  disabled = false,
  className = ''
}) => {
  const isCarousel = format.toLowerCase().includes('carousel') || format.toLowerCase().includes('карусель');
  const isReels = format.toLowerCase().includes('reels') || format.toLowerCase().includes('рилс');

  const quickActions = [
    {
      icon: Edit,
      label: 'Улучши текст',
      message: 'Проанализируй текущий текст и предложи улучшения: сделай его более вовлекающим, исправь структуру и усиль ключевые мысли.',
      color: 'text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
    },
    {
      icon: MessageSquare,
      label: 'Измени стиль',
      message: 'Перепиши этот текст в другом стиле (например, более дерзком, экспертном или дружелюбном), сохранив основной смысл.',
      color: 'text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
    },
    ...(isCarousel 
      ? [
          {
            icon: Zap,
            label: 'Нарисуй карточки',
            message: 'Нарисуй карусель на основе этого текста.',
            color: 'text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
          },
          {
            icon: Target,
            label: 'Улучши заголовок',
            message: 'Предложи 5 вариантов более сильных и цепляющих заголовков для первой карточки, чтобы повысить CTR карусели.',
            color: 'text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20'
          }
        ]
      : isReels
        ? [
            {
              icon: Zap,
              label: 'Улучши сценарий',
              message: 'Проанализируй текущий текст и преврати его в динамичный сценарий для Reels с указанием кадров, текста на экране и переходов.',
              color: 'text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20'
            },
            {
              icon: Target,
              label: 'Добавь хук',
              message: 'Придумай 3 варианта интригующего начала (хука) для этого видео, чтобы зритель не пролистал его в первые секунды.',
              color: 'text-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/20'
            }
          ]
        : [
            {
              icon: Target,
              label: 'Усили заголовок',
              message: 'Предложи 5 вариантов более мощных и интригующих заголовков для этого поста, которые заставят прочитать его до конца.',
              color: 'text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20'
            },
            {
              icon: Lightbulb,
              label: 'Добавь призыв',
              message: 'Предложи несколько вариантов сильного призыва к действию (CTA) для этого поста, чтобы повысить количество комментариев и сохранений.',
              color: 'text-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/20'
            }
          ]
    )
  ];

  const handleActionClick = (message: string) => {
    if (!disabled) {
      onSendMessage(message);
    }
  };

  return (
    <div className={`grid grid-cols-2 gap-2 ${className}`}>
      {quickActions.map((action) => (
        <button
          key={action.label}
          onClick={() => handleActionClick(action.message)}
          disabled={disabled}
          className={`flex items-center space-x-2 p-3 rounded-lg border border-gray-200 dark:border-gray-600 
                     transition-colors text-left ${action.color} 
                     disabled:opacity-50 disabled:cursor-not-allowed
                     hover:border-gray-300 dark:hover:border-gray-500
                     bg-white dark:bg-gray-800`}
          title={`${action.label} - Нажмите для отправки подробного запроса`}
        >
          <action.icon className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {action.label}
          </span>
        </button>
      ))}
    </div>
  );
}; 